import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import ScreenRouteNames from "@/constants/screen-route-names";

/**
 * Sets a unique, descriptive `document.title` on every route change, and announces the
 * new page to assistive technology.
 *
 * Why this exists:
 *
 * - **WCAG 2.4.2 Page Titled (Level A).** `client/index.html` shipped with no `<title>`
 *   element and no route ever set one, so every page in the product was untitled. Screen
 *   reader users rely on the title to know where they have landed, and it is the label
 *   shown in the browser tab, the history list and bookmarks.
 * - **WCAG 4.1.3 Status Messages (Level AA) / 2.4.3 Focus Order (Level A).** React Router
 *   swaps the view without a page load, so nothing is announced on navigation and focus
 *   stays wherever it was — often on a link in the sidebar that no longer relates to the
 *   content on screen. A polite live region carrying the new page name gives screen reader
 *   users the same "you have arrived somewhere new" cue a full page load would.
 *
 * The live region is rendered by `RouteAnnouncer` (see components/route-announcer.tsx) and
 * is mounted once, above the router outlet, so its text can change — a live region that is
 * inserted into the DOM already containing its message is not announced.
 */

const APP_NAME = "zConnect";

/** path segment → human readable page name, derived from the route registry. */
const buildTitleMap = (): Record<string, string> => {
  const map: Record<string, string> = {};

  for (const entry of Object.values(ScreenRouteNames)) {
    if (!entry.path) continue;
    const legacy = "legacyName" in entry ? entry.legacyName : undefined;
    map[entry.path] = legacy ?? toTitleCase(entry.path);
  }

  // Names that read better than the derived path, or that the registry does not cover.
  map["/"] = "Group code";
  map[ScreenRouteNames.LOGIN.path] = "Sign in";
  map[ScreenRouteNames.REGISTER.path] = "Create an account";
  map[ScreenRouteNames.ACCOUNT_RESET.path] = "Account reset";
  map[ScreenRouteNames.VERIFY_OTP.path] = "Verification code";
  map[ScreenRouteNames.MFA_LOGIN.path] = "Two-factor sign in";
  map[ScreenRouteNames.MY_ACCOUNT.path] = "My account";
  map[ScreenRouteNames.CARE_HISTORY.path] = "Care and claims history";
  map[ScreenRouteNames.ID_CARD.path] = "ID card";
  map[ScreenRouteNames.CSR_CHAT.path] = "Member support chat";
  map[ScreenRouteNames.ACCESS_DENIED.path] = "Access denied";
  map[ScreenRouteNames.SESSION_EXPIRED.path] = "Session expired";
  map[ScreenRouteNames.ERROR.path] = "Something went wrong";

  return map;
};

const toTitleCase = (path: string) =>
  path
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const TITLE_MAP = buildTitleMap();

/** Resolve the most specific known page name for a pathname. */
export function getPageTitle(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);

  // Deepest segment first, so /cost-estimator/results prefers "Results".
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const name = TITLE_MAP[segments[index]];
    if (name) {
      // A nested results view reads better qualified by its parent.
      if (index > 0 && TITLE_MAP[segments[index - 1]]) {
        return `${TITLE_MAP[segments[index - 1]]} — ${name}`;
      }
      return name;
    }
  }

  if (segments.length === 0) return TITLE_MAP["/"];
  return toTitleCase(segments[segments.length - 1]);
}

/**
 * @returns the current page name, so a caller can render it into a live region.
 */
export default function useDocumentTitle(): string {
  const { pathname } = useLocation();
  const pageTitle = getPageTitle(pathname);
  const isFirstRender = useRef(true);

  useEffect(() => {
    document.title = `${pageTitle} — ${APP_NAME}`;
    isFirstRender.current = false;
  }, [pageTitle]);

  return pageTitle;
}
